import { useMutation } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";
import {
  WorkflowService,
  AssistantsService,
  KnowledgeBasesV2Service,
  CaseManagementService,
  ExportWarningType,
} from "@/generated";
import { useSpotUser } from "@/utils/spotUser";
import { SpotToast } from "@/components/SpotToast";

function responseHeader(response: unknown, name: string): string | undefined {
  if (!response || typeof response !== "object" || !("headers" in response)) {
    return undefined;
  }
  const value = (response as AxiosResponse).headers?.[name];
  return typeof value === "string" ? value : undefined;
}

export type EntityType = "workflow" | "assistant" | "kb" | "inbox";

interface ExportEntityParams {
  entityId: string;
  entityName: string;
  entityType: EntityType;
  selectedVersionId?: string | null;
  // Operator hint persisted into the bundle manifest. Target import can override.
  setActiveOnImport?: boolean;
  // Pull referenced sub-flows / KBs / files into the .nx (default true).
  includeDependencies?: boolean;
}

const ENTITY_LABELS: Record<EntityType, string> = {
  workflow: "flow",
  assistant: "agent",
  kb: "knowledge base",
  inbox: "inbox",
};

export function useExportEntity({ onSuccess }: { onSuccess?: () => void }) {
  const { spotGroup } = useSpotUser();

  const exportMutation = useMutation({
    mutationFn: async ({
      entityId,
      entityName,
      entityType,
      selectedVersionId,
      setActiveOnImport,
      includeDependencies,
    }: ExportEntityParams) => {
      // Agents: omitted version_id -> all versions (the backend default);
      // "current" is spelled "latest" on the wire. Other entities keep the
      // legacy mapping (omitted -> current).
      const versionId =
        entityType === "assistant"
          ? selectedVersionId === "all"
            ? undefined
            : selectedVersionId === "current"
              ? "latest"
              : selectedVersionId
          : selectedVersionId === "current"
            ? undefined
            : selectedVersionId;

      const entityLabel = ENTITY_LABELS[entityType];

      const baseQuery: Record<string, string | boolean> = {};
      if (versionId) baseQuery.version_id = versionId;
      if (setActiveOnImport) baseQuery.set_active_on_import = true;
      // only send when opting out — true is the backend default
      if (includeDependencies === false) baseQuery.include_dependencies = false;
      const query = Object.keys(baseQuery).length ? baseQuery : undefined;

      let response;
      if (entityType === "workflow") {
        response = await WorkflowService.exportWorkflow({
          path: {
            group_id: spotGroup.id,
            workflow_id: entityId,
          },
          query: query as any,
        });
      } else if (entityType === "assistant") {
        response = await AssistantsService.exportAssistant({
          path: {
            group_id: spotGroup.id,
            assistant_id: entityId,
          },
          query: query as any,
        });
      } else if (entityType === "inbox") {
        response = await CaseManagementService.exportInbox({
          path: {
            group_id: spotGroup.id,
            inbox_id: entityId,
          },
          query: query as any,
        });
      } else {
        response = await KnowledgeBasesV2Service.exportKnowledgeBaseV2({
          path: {
            group_id: spotGroup.id,
            knowledge_base_id: entityId,
          },
          query: query as any,
        });
      }

      return { response, entityName, entityLabel };
    },
    onMutate: (variables) => {
      const toastId = SpotToast.loading(
        variables.entityType === "kb"
          ? "Preparing your export"
          : "Preparing your export"
      );
      return { toastId };
    },
    onSuccess: ({ response, entityName }, variables, context) => {
      const exportWarning = responseHeader(response, "x-export-warning");

      if (exportWarning === ExportWarningType.KB_TOO_LARGE) {
        SpotToast.warn(
          {
            title: "Exported without documents",
            description:
              "This knowledge base is larger than 15 MB, so it was exported with its settings only. Its documents were not included.",
          },
          undefined,
          undefined,
          context?.toastId
        );
      } else {
        SpotToast.success(
          "Your download will start shortly.",
          undefined,
          undefined,
          context?.toastId
        );
      }
      // the .nx is plaintext YAML — write the raw string verbatim
      const blob = new Blob([response.data as unknown as string], {
        type: "application/octet-stream",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const timestamp = new Date().toISOString();
      link.download = `${timestamp}-${entityName.toLowerCase().replace(/\s+/g, "-")}-export.nx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      onSuccess?.();
    },
    onError: (error: unknown, variables, context) => {
      const axiosError = error as { response?: { data?: { detail?: string } } };
      const entityLabel = ENTITY_LABELS[variables.entityType];
      const errorMessage =
        axiosError?.response?.data?.detail ||
        `Error exporting the ${entityLabel}`;
      SpotToast.error(errorMessage, undefined, undefined, context?.toastId);
    },
  });

  return exportMutation;
}
